import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUser,
  FiPhone,
  FiSmartphone,
  FiTablet,
  FiTool,
  FiBookOpen,
  FiMoreHorizontal,
  FiUploadCloud,
  FiFilm,
  FiX,
  FiSend,
  FiShield,
  FiCheck,
  FiAlertCircle,
  FiSearch,
  FiPlus,
  FiLogIn
} from 'react-icons/fi';
import { studentComplaintAPI } from '../services/api';
import tncLogo from '../assets/tnc-logo.png';
import appStoreIcon from '../assets/app-store.png';
import './StudentPortal.css';

const MAX_FILES = 5;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
const MAX_COMPLAINT_CHARS = 2000;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'];
const ALLOWED_TYPES = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES];

const isVideoFile = (file) => file.type.startsWith('video/');

// Android and Apple marks. react-icons only ships monochrome platform glyphs,
// so these stay inline to keep their brand colour.
const AndroidMark = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <g fill="none" stroke="#3ddc84" strokeWidth="1.5" strokeLinecap="round">
      <line x1="8.1" y1="4" x2="9.4" y2="6.1" />
      <line x1="15.9" y1="4" x2="14.6" y2="6.1" />
    </g>
    <path
      fill="#3ddc84"
      fillRule="evenodd"
      d="M12 5.6c-3.35 0-6.05 2.42-6.05 5.4v.7h12.1v-.7c0-2.98-2.7-5.4-6.05-5.4Zm-2.45 2.6a.82.82 0 1 0 0 1.64.82.82 0 0 0 0-1.64Zm4.9 0a.82.82 0 1 0 0 1.64.82.82 0 0 0 0-1.64Z"
    />
    <rect x="5.95" y="13" width="12.1" height="6.6" rx="2.1" fill="#3ddc84" />
  </svg>
);

const AppleMark = () => (
  <img className="sp-brand-img" src={appStoreIcon} alt="" />
);

// iPad reads as a portrait tablet with a home button, which is what separates
// it from the Android tablet option beside it.
const IpadIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M6 2h12a2.5 2.5 0 0 1 2.5 2.5v15A2.5 2.5 0 0 1 18 22H6a2.5 2.5 0 0 1-2.5-2.5v-15A2.5 2.5 0 0 1 6 2Zm6 1.6a.7.7 0 1 1 0 1.4.7.7 0 0 1 0-1.4ZM5.5 6.1h13v10.8h-13Zm6.5 11.7a1.15 1.15 0 1 1 0 2.3 1.15 1.15 0 0 1 0-2.3Z"
    />
  </svg>
);

// These values are stored in the existing os_version / phone_model columns, so
// the admin panel and Slack payloads keep their current shape.
const OS_TYPES = [
  { value: 'Android', icon: AndroidMark },
  { value: 'Apple', icon: AppleMark }
];

const DEVICE_TYPES = [
  { value: 'Phone', icon: FiSmartphone },
  { value: 'Tablet', icon: FiTablet },
  { value: 'iPad', icon: IpadIcon }
];

// Stored in student_complaints.complaint_category. The ticket keeps its
// "Student Complaints" complaint_type_id so admin grouping is unaffected.
const COMPLAINT_CATEGORIES = [
  { value: 'Technical', label: 'Technical', icon: FiTool },
  { value: 'Content Related', label: 'Content', icon: FiBookOpen },
  { value: 'Other', label: 'Other', icon: FiMoreHorizontal }
];

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const EMPTY_FORM = {
  student_name: '',
  student_phone: '',
  complaint_text: '',
  phone_model: '',
  os_version: '',
  complaint_category: ''
};

const StudentComplaint = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [screenshots, setScreenshots] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const navigate = useNavigate();

  // Load reCAPTCHA script
  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'YOUR_SITE_KEY';

    if (document.querySelector(`script[src*="recaptcha"]`)) {
      setRecaptchaReady(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const checkReady = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            setRecaptchaReady(true);
            clearInterval(checkReady);
          });
        }
      }, 100);
    };
    document.head.appendChild(script);
  }, []);

  // Object URLs are created per file and revoked when the selection changes
  useEffect(() => {
    const urls = screenshots.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [screenshots]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList);
    if (!incoming.length) return;

    const accepted = [];
    for (const file of incoming) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(`${file.name} is not supported. Use an image (JPEG, PNG, GIF, WebP) or a video (MP4, MOV, WebM).`);
        return;
      }
      const limit = isVideoFile(file) ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
      if (file.size > limit) {
        setError(
          isVideoFile(file)
            ? `${file.name} is larger than 50MB. Please attach a shorter recording.`
            : `${file.name} is larger than 5MB. Please attach a smaller image.`
        );
        return;
      }
      accepted.push(file);
    }

    if (screenshots.length + accepted.length > MAX_FILES) {
      setError(`You can attach up to ${MAX_FILES} files.`);
      return;
    }

    setScreenshots([...screenshots, ...accepted]);
    setError('');
  };

  const handleFileChange = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer && e.dataTransfer.files) {
      addFiles(e.dataTransfer.files);
    }
  };

  const removeScreenshot = (index) => {
    setScreenshots(screenshots.filter((_, i) => i !== index));
  };

  const getRecaptchaToken = async () => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'YOUR_SITE_KEY';

    if (!window.grecaptcha || !recaptchaReady) {
      console.warn('reCAPTCHA not ready, proceeding without token');
      return null;
    }

    try {
      const token = await window.grecaptcha.execute(siteKey, { action: 'submit_complaint' });
      return token;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate phone number
    if (!/^[0-9]{10}$/.test(formData.student_phone)) {
      setError('Phone number must be exactly 10 digits');
      setLoading(false);
      return;
    }

    if (!OS_TYPES.some((os) => os.value === formData.os_version)) {
      setError('Please select your operating system');
      setLoading(false);
      return;
    }

    if (!DEVICE_TYPES.some((device) => device.value === formData.phone_model)) {
      setError('Please select your device type');
      setLoading(false);
      return;
    }

    if (!COMPLAINT_CATEGORIES.some((c) => c.value === formData.complaint_category)) {
      setError('Please select the type of complaint');
      setLoading(false);
      return;
    }

    try {
      const recaptchaToken = await getRecaptchaToken();

      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('student_name', formData.student_name);
      submitData.append('student_phone', formData.student_phone);
      submitData.append('complaint_text', formData.complaint_text);
      submitData.append('phone_model', formData.phone_model);
      submitData.append('os_version', formData.os_version);
      submitData.append('complaint_category', formData.complaint_category);
      submitData.append('recaptchaToken', recaptchaToken);

      // Add screenshots
      screenshots.forEach((file) => {
        submitData.append('screenshots', file);
      });

      const response = await studentComplaintAPI.submitComplaint(submitData);

      setSuccess(response.data);
    } catch (err) {
      const errorData = err.response?.data;
      const errorMessage = errorData?.message || errorData?.error || 'Failed to submit complaint. Please try again.';
      setError(errorMessage);

      if (errorMessage.includes('reCAPTCHA') || errorMessage.includes('bot')) {
        setError('Security verification failed. Please refresh the page and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const brandMark = (
    <span
      className="sp-mark"
      role="img"
      aria-label="TNC Nursing Classes, Patna"
      style={{ backgroundImage: `url(${tncLogo})` }}
    />
  );

  if (success) {
    return (
      <div className="student-portal">
        <div className="sp-card fade-in">
          <div className="sp-success-head">
            <div className="sp-success-icon">
              <FiCheck />
            </div>
            <h2>Complaint received</h2>
            <p className="sp-subtitle">We have your complaint and will look into it.</p>
          </div>

          <div className="sp-receipt">
            <div className="sp-receipt-row">
              <span className="sp-receipt-label">Ticket ID</span>
              <span className="sp-receipt-value">{success.ticket_id}</span>
            </div>
            <div className="sp-receipt-row">
              <span className="sp-receipt-label">Phone number</span>
              <span className="sp-receipt-value">{success.phone_number}</span>
            </div>
          </div>

          <div className="sp-note">
            <FiAlertCircle />
            <span>Save both of these. You will need them to track your complaint.</span>
          </div>

          <div className="sp-foot">
            <button onClick={() => navigate('/track-complaint')} className="sp-submit">
              <FiSearch /> Track my complaint
            </button>
            <button
              onClick={() => {
                setSuccess(null);
                setFormData(EMPTY_FORM);
                setScreenshots([]);
              }}
              className="sp-btn-secondary"
            >
              <FiPlus /> Submit another complaint
            </button>
          </div>

          <div className="sp-links">
            <button onClick={() => navigate('/login')} className="sp-link">
              Staff login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="student-portal">
      <div className="sp-card fade-in">
        <div className="sp-head">
          <h2 className="sp-title">
            {brandMark}
            TNC Help Portal
          </h2>
          <p className="sp-subtitle">Every student is important to us, we are here to help.</p>
        </div>

        {error && (
          <div className="sp-error-banner" role="alert">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <section className="sp-section">
            <div className="sp-row">
              <div className="sp-field">
                <label className="sp-label" htmlFor="student_name">
                  Full name <span className="sp-req">*</span>
                </label>
                <div className="sp-input-wrap">
                  <FiUser />
                  <input
                    id="student_name"
                    type="text"
                    name="student_name"
                    className="sp-input"
                    value={formData.student_name}
                    onChange={handleChange}
                    required
                    autoFocus
                    autoComplete="name"
                    placeholder="Your full name"
                  />
                </div>
              </div>

              <div className="sp-field">
                <label className="sp-label" htmlFor="student_phone">
                  Phone number <span className="sp-req">*</span>
                </label>
                <div className="sp-input-wrap">
                  <FiPhone />
                  <input
                    id="student_phone"
                    type="tel"
                    name="student_phone"
                    className="sp-input"
                    value={formData.student_phone}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{10}"
                    maxLength="10"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder="10 digit number"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="sp-section">
            <div className="sp-stack">
              <div className="sp-field">
                <span className="sp-label">
                  Operating system <span className="sp-req">*</span>
                </span>
                <div className="sp-options sp-options-2 sp-options-brand" role="radiogroup" aria-label="Operating system">
                  {OS_TYPES.map(({ value, icon: Icon }) => (
                    <label
                      key={value}
                      className={`sp-pill ${formData.os_version === value ? 'sp-on' : ''}`}
                    >
                      <input
                        type="radio"
                        name="os_version"
                        value={value}
                        checked={formData.os_version === value}
                        onChange={handleChange}
                      />
                      <Icon />
                      <span>{value}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="sp-field">
                <span className="sp-label">
                  Device type <span className="sp-req">*</span>
                </span>
                <div className="sp-options sp-options-3" role="radiogroup" aria-label="Device type">
                  {DEVICE_TYPES.map(({ value, icon: Icon }) => (
                    <label
                      key={value}
                      className={`sp-pill ${formData.phone_model === value ? 'sp-on' : ''}`}
                    >
                      <input
                        type="radio"
                        name="phone_model"
                        value={value}
                        checked={formData.phone_model === value}
                        onChange={handleChange}
                      />
                      <Icon />
                      <span>{value}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="sp-section">
            <div className="sp-stack">
              <div className="sp-field">
                <span className="sp-label">
                  Type of complaint <span className="sp-req">*</span>
                </span>
                <div className="sp-options sp-options-3 sp-options-tall" role="radiogroup" aria-label="Type of complaint">
                  {COMPLAINT_CATEGORIES.map(({ value, label, icon: Icon }) => (
                    <label
                      key={value}
                      className={`sp-pill ${formData.complaint_category === value ? 'sp-on' : ''}`}
                    >
                      <input
                        type="radio"
                        name="complaint_category"
                        value={value}
                        checked={formData.complaint_category === value}
                        onChange={handleChange}
                      />
                      <Icon />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="sp-field">
                <label className="sp-label" htmlFor="complaint_text">
                  Describe the problem <span className="sp-req">*</span>
                </label>
                <textarea
                  id="complaint_text"
                  name="complaint_text"
                  className="sp-textarea"
                  value={formData.complaint_text}
                  onChange={handleChange}
                  required
                  maxLength={MAX_COMPLAINT_CHARS}
                  placeholder="What were you doing when it happened? What did you expect to see instead?"
                />
                <span className="sp-counter">
                  {formData.complaint_text.length} / {MAX_COMPLAINT_CHARS}
                </span>
              </div>

              <div className="sp-field">
                <span className="sp-label">
                  Screenshots or video <span className="sp-optional">&mdash; optional</span>
                </span>

                <label
                  className={`sp-drop ${dragging ? 'sp-hot' : ''}`}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setDragging(false);
                  }}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    name="screenshots"
                    accept={ALLOWED_TYPES.join(',')}
                    multiple
                    onChange={handleFileChange}
                  />
                  <span className="sp-drop-icon">
                    <FiUploadCloud />
                  </span>
                  <span className="sp-drop-title">
                    Drag files here or <b>browse</b>
                  </span>
                  <span className="sp-drop-meta">
                    {screenshots.length
                      ? `${screenshots.length} of ${MAX_FILES} added · ${formatBytes(
                          screenshots.reduce((sum, file) => sum + file.size, 0)
                        )} total`
                      : 'Images up to 5 MB · videos up to 50 MB · 5 files max'}
                  </span>
                </label>

                {screenshots.length > 0 && (
                  <div className="sp-thumbs">
                    {screenshots.map((file, index) => (
                      <figure className="sp-thumb" key={`${file.name}-${index}`}>
                        {isVideoFile(file) ? (
                          <>
                            <video src={previews[index]} muted playsInline preload="metadata" />
                            <span className="sp-thumb-badge">
                              <FiFilm />
                            </span>
                          </>
                        ) : (
                          <img src={previews[index]} alt={file.name} />
                        )}
                        <figcaption>{formatBytes(file.size)}</figcaption>
                        <button
                          type="button"
                          className="sp-thumb-remove"
                          aria-label={`Remove ${file.name}`}
                          onClick={() => removeScreenshot(index)}
                        >
                          <FiX />
                        </button>
                      </figure>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          <div className="sp-foot">
            <button type="submit" className="sp-submit" disabled={loading}>
              <FiSend /> {loading ? 'Submitting...' : 'Submit complaint'}
            </button>

            <p className="sp-legal">
              <FiShield />
              <span>
                Protected by reCAPTCHA. Google&rsquo;s{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                  Terms
                </a>{' '}
                apply.
              </span>
            </p>
          </div>
        </form>

        <div className="sp-links">
          Already submitted one?{' '}
          <button onClick={() => navigate('/track-complaint')} className="sp-link">
            Track your complaint
          </button>{' '}
          &middot;{' '}
          <button onClick={() => navigate('/login')} className="sp-link">
            <FiLogIn style={{ verticalAlign: '-2px', marginRight: '3px' }} />
            Staff login
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentComplaint;
